import _ from 'lodash'
import PT from 'prop-types'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Layout, Size, Sizes, Widget as IWidget, WidgetContainerProps } from 'index.d'
import { BreakpointPropType, LabelsPropType, LayoutPropType, WidgetPropType } from 'index.pt'
import Widget from './Widget'

export const WidgetContainerDiv = styled.div`
  .c-d-widget-border {
    border: 1px solid var(--navds-semantic-color-border);
    border-radius: 4px;
  }    
`

const WidgetContainer: React.FC<WidgetContainerProps> = ({
  currentBreakpoint, editMode, isDragging, highContrast, labels, layout, myWidgets, onWidgetDelete, onWidgetUpdate, onWidgetResize,
  onWidgetFullFocus, onWidgetRestoreFocus, rowHeight, widget
}: WidgetContainerProps): JSX.Element => {
  const [sizes, setSizes] = useState<Sizes>({ lg: {}, md: {}, sm: {} })
  const [mouseOver, setMouseOver] = useState<boolean>(false)
  const [mounted, setMounted] = useState<boolean>(false)
  const [mode, setMode] = useState<string>('view')

  useEffect(() => {
    const getSize: () => Size = () => {
      return {
        width: document.getElementById('widget-' + layout.i)!.offsetWidth,
        height: document.getElementById('widget-' + layout.i)!.offsetHeight
      }
    }

    if (!mounted) {
      if (document.getElementById('widget-' + layout.i)) {
        const newSize: Size = getSize()
        const oldSizes: Sizes = _.cloneDeep(sizes)
        if (_.isEmpty(oldSizes[currentBreakpoint]) || (
          ((oldSizes[currentBreakpoint].height !== newSize.height) ||
          (oldSizes[currentBreakpoint].width !== newSize.width)))) {
          oldSizes[currentBreakpoint] = newSize
          setSizes(oldSizes)
        }
      }
      setMounted(true)
    }
  }, [mounted, layout, sizes, currentBreakpoint])

  useEffect(() => {
    if (!isDragging) {
      if (mode === 'view' && editMode && mouseOver) {
        setMode('edit')
      }
      if (mode === 'edit' && !mouseOver) {
        setMode('view')
      }
    } else {
      if (mode !== 'edit') {
        setMode('edit')
      }
    }
  }, [editMode, isDragging, mode, mouseOver])

  const onUpdate = (widget: IWidget): void => {
    onWidgetUpdate!(widget, layout)
  }

  const onDelete = (): void => {
    onWidgetDelete!(layout)
  }

  const onFullFocus = (): void => {
    onWidgetFullFocus!(widget)
  }

  const onRestoreFocus = (): void => {
    onWidgetRestoreFocus!()
  }

  const onResize = (width?: number, height?: number): void => {
    if (!height || !width) {
      return
    }
    if (_.isFunction(onWidgetResize)) {
      // these 10 are padding/margin added to each h
      let newH: number = Math.ceil((height + 10) / (rowHeight + 10))

      // do not shrink below minimum H
      if (newH < layout.minH) {
        // console.log('New h ' + newH + ' rejected because minH is ' + newLayout.minH)
        newH = layout.minH
      }

      if (newH > layout.maxH) {
        // console.log('New h ' + newH + ' rejected because maxH is ' + newLayout.maxH)
        newH = layout.maxH
      }

      // in edit mode, don't make it smaller than view mode
      if (mode === 'edit' && newH < layout.h) {
        // console.log('New h ' + newH + ' rejected because edit mode and h is ' + newLayout.h)
        newH = layout.h
      }
      if (mode !== 'edit' && newH !== layout.h) {
        // console.log('Resized ' + newLayout.i + ' from h ' + newLayout.h + ' to ' + newH)
        const newLayout: Layout = _.cloneDeep(layout)
        newLayout.h = newH
        onWidgetResize(newLayout)
      }
    }
  }

  if (!widget) {
    return <div />
  }

  let backgroundColor: string = widget.options.backgroundColor || 'transparent'
  if (editMode) {
    backgroundColor = 'white'
  }

  return (

    <WidgetContainerDiv
      style={{ backgroundColor }}
      onMouseEnter={() => setMouseOver(true)}
      onMouseLeave={() => setMouseOver(false)}
    >
      <Widget
        highContrast={highContrast}
        layout={layout}
        labels={labels}
        mode={mode}
        setMode={setMode}
        onDelete={onDelete}
        onUpdate={onUpdate}
        onResize={onResize}
        onFullFocus={onFullFocus}
        onRestoreFocus={onRestoreFocus}
        myWidgets={myWidgets}
        widget={widget}
      />
    </WidgetContainerDiv>
  )
}

WidgetContainer.propTypes = {
  currentBreakpoint: BreakpointPropType.isRequired,
  editMode: PT.bool.isRequired,
  layout: LayoutPropType.isRequired,
  labels: LabelsPropType.isRequired,
  onWidgetUpdate: PT.func,
  onWidgetDelete: PT.func,
  onWidgetResize: PT.func,
  onWidgetFullFocus: PT.func,
  onWidgetRestoreFocus: PT.func,
  rowHeight: PT.number.isRequired,
  widget: WidgetPropType.isRequired
}

export default WidgetContainer
